import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { iconSizes } from "@stubhub/broadway-types";
import { Button, Heading, Modal, ModalBody, ModalFooter, ModalHeader, Text } from "@stubhub/broadway-web";
import { PriceTagFillIcon, FanProtectFillIcon, TicketMoneyFillIcon } from "@stubhub/broadway-web/icons/functional";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { Content, FormatContent, useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { aipBottomsheetRecipe } from "src/website-apps/responsive/stubhub-performer/components/aip-bottomsheet/AipBottomsheet.recipe";
import {
  FAN_PROTECT_TRADEMARK,
  VIAGOGO_BRAND_NAME,
} from "src/website-apps/shared/components/brand-guarantee/constants";

interface AipBottomsheetProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  titleContent: ReactNode;
  copyVariant: string;
}

export const AipBottomsheet = ({ isOpen, onOpenChange, titleContent, copyVariant }: AipBottomsheetProps) => {
  const storePageEvent = useStorePageEvent();
  const showFanProtect = useSimpleFeatureTernary(FeatureSetEnum.TMPerformerFanProtectInAIPBottomsheet, false, true);

  const isStubhub = useIsStubhub();
  const guaranteeName = isStubhub ? FAN_PROTECT_TRADEMARK : VIAGOGO_BRAND_NAME;

  const titleClasses = aipBottomsheetRecipe({ placement: "title" });
  const rowClasses = aipBottomsheetRecipe({ placement: "row" });
  const fanProtectRowClasses = aipBottomsheetRecipe({ placement: "row", iconColor: "info" });

  const bodyContent = useContent("cid$#4167499#"); // "We show the full ticket price upfront, including all fees."
  const ctaContent = useContent("cid$#4167500#"); // "Continue"
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      storePageEvent(PE_TYPE.OPEN, PE_KEYS.AIP_BOTTOMSHEET_IMPRESSION, copyVariant);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, storePageEvent, copyVariant]);

  const handleOpenChange = useCallback(
    (newIsOpen: boolean) => {
      if (isOpen && !newIsOpen) {
        storePageEvent(PE_TYPE.CLOSE, PE_KEYS.AIP_BOTTOMSHEET_DISMISS, copyVariant);
      }
      onOpenChange(newIsOpen);
    },
    [isOpen, onOpenChange, storePageEvent, copyVariant],
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={handleOpenChange} dismissible data-testid={DataTestIds.AipBottomsheet}>
      <ModalHeader />
      <ModalBody>
        <div className={titleClasses.content}>
          <div className={titleClasses.iconContainer}>
            <TicketMoneyFillIcon size={iconSizes[5]} />
          </div>
          <Heading variant="header" as="h2">
            {titleContent}
          </Heading>
          {showFanProtect ? (
            <div className={titleClasses.detailBody}>
              <div className={titleClasses.row}>
                <div className={rowClasses.iconContainer}>
                  <PriceTagFillIcon size={iconSizes[2]} />
                </div>
                <Text>
                  {/* "We show the full ticket price upfront, <b>including all fees</b>" */}
                  <Content id="cid$#4370274#" raw />
                </Text>
              </div>
              <div className={titleClasses.row}>
                <div className={fanProtectRowClasses.iconContainer}>
                  <FanProtectFillIcon size={iconSizes[2]} />
                </div>
                <Text>
                  {/* "Your tickets are 100% protected by <b>{0} Guarantee</b>" */}
                  <FormatContent id="cid$#4370281#" params={[guaranteeName]} raw />
                </Text>
              </div>
            </div>
          ) : (
            <Text>{bodyContent}</Text>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          classes={{ root: titleClasses.button! }}
          onClick={() => {
            // CTA close bypasses handleOpenChange so it is not counted as a dismiss.
            storePageEvent(PE_TYPE.CLICK, PE_KEYS.AIP_BOTTOMSHEET_CTA_CLICK, copyVariant);
            onOpenChange(false);
          }}
        >
          {ctaContent}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
